import { Plus, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
  PostingScheduleConfig,
  PostingSlot,
  PlatformType,
  DEFAULT_POSTING_SLOTS,
} from "../models/Platform";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

interface PostingScheduleEditorProps {
  connectionId: string;
  platform: PlatformType;
  initialSchedule?: PostingScheduleConfig;
  onSave: (schedule: PostingScheduleConfig) => Promise<void>;
  onCancel: () => void;
}

// Grid row: one time with toggleable days
interface TimeRow {
  id: string;
  hour: number;
  minute: number;
  days: number[]; // active dayOfWeek values (0-6)
}

// Day columns in Mon-Sun order (matches the screenshot pattern)
const DAY_COLUMNS: { dow: number; label: string; initial: string }[] = [
  { dow: 1, label: "Mon", initial: "M" },
  { dow: 2, label: "Tue", initial: "T" },
  { dow: 3, label: "Wed", initial: "W" },
  { dow: 4, label: "Thu", initial: "T" },
  { dow: 5, label: "Fri", initial: "F" },
  { dow: 6, label: "Sat", initial: "S" },
  { dow: 0, label: "Sun", initial: "S" },
];

// Generate 30-min increment time options
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = i % 2 === 0 ? 0 : 30;
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const displayMinute = String(minute).padStart(2, "0");
  return {
    value: `${hour}:${minute}`,
    label: `${displayHour}:${displayMinute} ${period}`,
    hour,
    minute,
  };
});

const COMMON_TIMEZONES_BASE = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Vancouver",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Kolkata",
  "Australia/Sydney",
  "Pacific/Auckland",
];

function getTimezoneList(selected: string): string[] {
  if (COMMON_TIMEZONES_BASE.includes(selected)) return COMMON_TIMEZONES_BASE;
  return [selected, ...COMMON_TIMEZONES_BASE];
}


function getDefaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "America/New_York";
  }
}

let nextRowId = 1;
function generateRowId(): string {
  return `row_${Date.now()}_${nextRowId++}`;
}

/** Convert flat PostingSlot[] → grouped TimeRow[] */
function slotsToRows(slots: PostingSlot[]): TimeRow[] {
  const map = new Map<string, TimeRow>();
  for (const slot of slots) {
    const key = `${slot.hour}:${slot.minute}`;
    const existing = map.get(key);
    if (existing) {
      if (!existing.days.includes(slot.dayOfWeek)) {
        existing.days.push(slot.dayOfWeek);
      }
    } else {
      map.set(key, {
        id: generateRowId(),
        hour: slot.hour,
        minute: slot.minute,
        days: [slot.dayOfWeek],
      });
    }
  }
  // Sort rows by time
  return Array.from(map.values()).sort(
    (a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute)
  );
}

/** Convert TimeRow[] → flat PostingSlot[] */
function rowsToSlots(rows: TimeRow[]): PostingSlot[] {
  let id = 1;
  const slots: PostingSlot[] = [];
  for (const row of rows) {
    for (const dow of row.days) {
      slots.push({
        id: `s${id++}`,
        dayOfWeek: dow,
        hour: row.hour,
        minute: row.minute,
      });
    }
  }
  return slots;
}

export function PostingScheduleEditor({
  platform,
  initialSchedule,
  onSave,
  onCancel,
}: PostingScheduleEditorProps) {
  const initialSlots = initialSchedule?.slots?.length
    ? initialSchedule.slots
    : DEFAULT_POSTING_SLOTS[platform] ?? [];

  const [rows, setRows] = useState<TimeRow[]>(() => slotsToRows(initialSlots));
  const [timezone, setTimezone] = useState(
    initialSchedule?.timezone ?? getDefaultTimezone()
  );
  const [saving, setSaving] = useState(false);

  const timezones = useMemo(() => getTimezoneList(timezone), [timezone]);

  const totalSlots = rows.reduce((sum, r) => sum + r.days.length, 0);

  const addRow = useCallback(() => {
    setRows((prev) => [
      ...prev,
      { id: generateRowId(), hour: 9, minute: 0, days: [1, 2, 3, 4, 5] },
    ]);
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const updateRowTime = useCallback((id: string, value: string) => {
    const [hour, minute] = value.split(":").map(Number);
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, hour, minute } : r))
    );
  }, []);

  const toggleDay = useCallback((rowId: string, dow: number) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const days = r.days.includes(dow)
          ? r.days.filter((d) => d !== dow)
          : [...r.days, dow];
        return { ...r, days };
      })
    );
  }, []);

  const resetToDefaults = useCallback(() => {
    const defaults = DEFAULT_POSTING_SLOTS[platform] ?? [];
    setRows(slotsToRows(defaults));
  }, [platform]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const slots = rowsToSlots(rows);
      await onSave({ slots, timezone });
    } finally {
      setSaving(false);
    }
  }, [rows, timezone, onSave]);

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Posts auto-schedule to the next available slot. You can override per
        post.
      </p>

      {/* Timezone */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Timezone
        </p>
        <div className="shrink-0">
          <Select value={timezone} onValueChange={setTimezone}>
            <SelectTrigger className="h-8 w-full sm:w-56 text-sm font-normal">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timezones.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz.replace(/_/g, " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Schedule */}
      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Schedule
          </p>
          <span className="text-xs text-muted-foreground tabular-nums">
            {totalSlots} post{totalSlots === 1 ? "" : "s"} / week
          </span>
        </div>

        <div className="rounded-lg border bg-background overflow-hidden">
          {/* Day headers */}
          <div className="grid grid-cols-[110px_1fr_28px] items-center gap-3 px-3 py-2 bg-muted/30 border-b">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Time
            </span>
            <div className="grid grid-cols-7 gap-1">
              {DAY_COLUMNS.map((col) => (
                <span
                  key={col.dow}
                  className="text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                  aria-label={col.label}
                >
                  {col.initial}
                </span>
              ))}
            </div>
            <span aria-hidden />
          </div>

          {/* Time rows */}
          <div className="divide-y divide-border">
            {rows.map((row) => (
              <ScheduleRow
                key={row.id}
                row={row}
                onTimeChange={(val) => updateRowTime(row.id, val)}
                onToggleDay={(dow) => toggleDay(row.id, dow)}
                onRemove={() => removeRow(row.id)}
              />
            ))}
            {rows.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No time slots yet. Add one below.
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border bg-muted/20 px-2 py-1.5 flex items-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addRow}
              className="h-8 text-xs font-normal text-muted-foreground hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add time
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={resetToDefaults}
              className="h-8 text-xs font-normal text-muted-foreground hover:text-foreground ml-auto"
            >
              Reset to defaults
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save schedule"}
        </Button>
      </div>
    </div>
  );
}

function ScheduleRow({
  row,
  onTimeChange,
  onToggleDay,
  onRemove,
}: {
  row: TimeRow;
  onTimeChange: (value: string) => void;
  onToggleDay: (dow: number) => void;
  onRemove: () => void;
}) {
  return (
    <div className="grid grid-cols-[110px_1fr_28px] items-center gap-3 px-3 py-2.5">
      <Select
        value={`${row.hour}:${row.minute}`}
        onValueChange={onTimeChange}
      >
        <SelectTrigger className="h-8 text-sm font-normal">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TIME_OPTIONS.map((t) => (
            <SelectItem key={t.value} value={t.value}>
              {t.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="grid grid-cols-7 gap-1">
        {DAY_COLUMNS.map((col) => {
          const active = row.days.includes(col.dow);
          return (
            <button
              key={col.dow}
              type="button"
              onClick={() => onToggleDay(col.dow)}
              aria-pressed={active}
              aria-label={col.label}
              className={cn(
                "h-8 rounded-md text-xs font-medium transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                active
                  ? "bg-foreground text-background hover:bg-foreground/90"
                  : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              {col.initial}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove time slot"
        className="h-7 w-7 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
